import { isEmpty } from 'radash';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { DateInput } from '@/components/core/DateInput';
import { useFormReset } from '@/dialogs/item-editor/Context/FormResetContext';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent, Validation } from '../types';

interface DateComponentProps extends EditorComponent {
    // Date specific props go here
    className?: string;
    openCalendarOnMount?: boolean;
}

const DateComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    gridMode = false,
    hidden = false,
    readonly = false,
    hideLabel = false,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    className,
    handleUrlChange,
    openCalendarOnMount = false,
}: DateComponentProps) => {

    const [focusDate, setFocusDate] = useState<boolean>(false);

    useFocusOnAttribute<HTMLInputElement>(
        { name: fieldPath },
        setFocusDate
    );

    // Reset focus state when form is being reset (after save)
    const { isResetting } = useFormReset();
    useEffect(() => {
        if (isResetting) {
            setFocusDate(false);
        }
    }, [isResetting]);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));

    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    const { control } = useFormContext();

    const placeholder = usePlaceholder(attribute);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    if (hidden) {
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!gridMode && !readonly}
        >
            <div className="tw-flex tw-flex-row">
                <Controller
                    control={control}
                    name={fieldPath}
                    render={({ field }) => (
                        <DateInput
                            id={fieldPath}
                            borderless={gridMode}
                            handleUrlChange={handleUrlChange}
                            placeholder={placeholder}
                            disabled={readonly}
                            focusDate={focusDate}
                            openOnMount={openCalendarOnMount}
                            selectedDate={field.value}
                            gridMode={gridMode}
                            onChange={value => {
                                if (value) {
                                    // The time of the date is set to 00:00 with the locale timezone, set it to the middle of the day
                                    // so we don't lose a day in case it was +GMT
                                    value.setHours(12);
                                    // We only want the Date portion of the DateTime object
                                    field.onChange(value.toISOString().slice(0, 10));
                                } else {
                                    // Clear the date when value is null
                                    field.onChange(null);
                                }
                            }}
                            className={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-white ${className}`}
                        />
                    )}
                />
            </div>
        </Layout>
    );
};

export { DateComponent };
